/**
 * Pantalla de inicio de sesión.
 *
 * Formulario validado con Zod (`loginSchema`): si la validación local falla, se
 * muestran los mensajes y no se llama a la API. En envío válido dispara
 * `useLogin` (`POST /auth/login`); el éxito deja las cookies httpOnly puestas y
 * re-hidrata `/auth/me` (lo hace el hook), tras lo cual navega al destino
 * original (o `/`). Las credenciales inválidas (`ApiError` 401) se muestran como
 * error del formulario.
 *
 * Sin lógica de datos en el JSX: los datos/efectos vienen del hook; el
 * componente solo presenta. La navegación tras autenticar es **reactiva**: en
 * cuanto el contexto pasa a `authenticated` (tras rehidratar `/auth/me`), la
 * pantalla redirige al destino con `<Navigate>`, evitando carreras entre el
 * refetch y un `navigate` imperativo. Usa elementos nativos accesibles
 * (`<form>`/`<label>`/`<input>`); la librería `components/ui` llega en la #44.
 */
import { type FormEvent, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

import { useLogin } from "../hooks/useLogin";
import { loginSchema } from "../schemas";

interface LocationState {
  from?: { pathname?: string };
}

/** Errores por campo derivados de la validación Zod. */
type FieldErrors = Partial<Record<"username" | "password", string>>;

export function LoginScreen() {
  const location = useLocation();
  const { status } = useAuth();
  const login = useLogin();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  // Destino al que volver tras autenticar (lo deja `RequireAuth`); default "/".
  const from = (location.state as LocationState | null)?.from?.pathname ?? "/";

  // Ya hay sesión (recién iniciada o el usuario llegó a /login estando dentro):
  // redirige al destino. Reactivo, sin competir con el refetch de /auth/me.
  if (status === "authenticated") {
    return <Navigate to={from} replace />;
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const result = loginSchema.safeParse({ username, password });
    if (!result.success) {
      const flattened = result.error.flatten().fieldErrors;
      const errors: FieldErrors = {};
      if (flattened.username?.[0]) {
        errors.username = flattened.username[0];
      }
      if (flattened.password?.[0]) {
        errors.password = flattened.password[0];
      }
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    login.mutate(result.data);
  }

  const submitError = login.isError ? messageFor(login.error) : null;

  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-sm rounded-lg bg-white p-8 shadow">
        <h1 className="text-2xl font-semibold text-brand">Láminas Dobleses</h1>
        <p className="mt-1 text-sm text-slate-500">Inicia sesión para continuar.</p>

        <form className="mt-6 space-y-4" onSubmit={handleSubmit} noValidate>
          <div className="space-y-1">
            <label htmlFor="username" className="block text-sm font-medium text-slate-700">
              Usuario
            </label>
            <input
              id="username"
              name="username"
              type="text"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              aria-invalid={fieldErrors.username ? true : undefined}
              aria-describedby={fieldErrors.username ? "username-error" : undefined}
              className="block w-full rounded-md border border-slate-300 px-3 py-2 text-slate-900 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
            {fieldErrors.username && (
              <p id="username-error" className="text-sm text-red-600">
                {fieldErrors.username}
              </p>
            )}
          </div>

          <div className="space-y-1">
            <label htmlFor="password" className="block text-sm font-medium text-slate-700">
              Contraseña
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              aria-invalid={fieldErrors.password ? true : undefined}
              aria-describedby={fieldErrors.password ? "password-error" : undefined}
              className="block w-full rounded-md border border-slate-300 px-3 py-2 text-slate-900 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
            {fieldErrors.password && (
              <p id="password-error" className="text-sm text-red-600">
                {fieldErrors.password}
              </p>
            )}
          </div>

          {submitError && (
            <p role="alert" className="text-sm text-red-600">
              {submitError}
            </p>
          )}

          <button
            type="submit"
            disabled={login.isPending}
            className="w-full rounded-md bg-brand px-4 py-2 font-medium text-white transition hover:bg-brand-600 disabled:opacity-60"
          >
            {login.isPending ? "Ingresando…" : "Ingresar"}
          </button>
        </form>
      </div>
    </main>
  );
}

/** Mensaje legible del error de login (mensaje del backend o uno genérico). */
function messageFor(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  return "No se pudo iniciar sesión. Inténtalo de nuevo.";
}
